import { Track } from "@/src/lib/tracking";
import { getDeviceParser, getDeviceType, getItem } from "utils";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { getValue } from "@features/firebase/initFirebase";
import {
  REMOTE_CONFIG_KEY_APP_MINIMUM_BUILD_NUMBER,
  REMOTE_CONFIG_KEY_CAN_CLOSE_UPGRADE_MODAL,
} from "@features/firebase/remoteConfigKeys";
import type { AppInitData } from "@/src/lib/bridge/types";

const APP_VERSION_UPDATE_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

function parseRemoteMinBuild(
  configJson: string,
  platform: string,
): number | null {
  try {
    const config = JSON.parse(configJson) as Record<string, unknown>;
    const raw = config[platform];
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function getCurrentAppBuildNumber(): number | null {
  if (typeof window === "undefined") {
    return null;
  }
  const injected = (window as { __APP_DEVICE_INFO__?: AppInitData })
    .__APP_DEVICE_INFO__;
  const fromInjected = injected?.buildNumber;
  const fromUa = getDeviceParser().buildNumber;
  const raw = fromInjected ?? fromUa;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function needsAppVersionUpdate(
  currentBuild: number,
  remoteMinBuild: number,
): boolean {
  return currentBuild < remoteMinBuild;
}

function canShowAppVersionUpdateByInterval(
  lastShown: string | number | null | undefined,
  now: number,
  intervalMs: number = APP_VERSION_UPDATE_INTERVAL_MS,
): boolean {
  if (lastShown == null || lastShown === "") {
    return true;
  }
  const last = Number(lastShown);
  if (!Number.isFinite(last)) {
    return true;
  }
  return now - last >= intervalMs;
}

async function fetchRemoteMinBuildForPlatform(): Promise<number | null> {
  const platform = getDeviceType();
  if (platform !== "ios" && platform !== "android") {
    return null;
  }
  const configValue = (
    await getValue(REMOTE_CONFIG_KEY_APP_MINIMUM_BUILD_NUMBER)
  ).asString();
  return parseRemoteMinBuild(configValue, platform);
}

export async function fetchCanCloseUpgradeModal(): Promise<boolean> {
  try {
    const configValue = await getValue(
      REMOTE_CONFIG_KEY_CAN_CLOSE_UPGRADE_MODAL,
    );
    const raw = configValue.asString();
    if (!raw) {
      return true;
    }
    return configValue.asBoolean();
  } catch (error) {
    Track.log(
      "warn",
      "fetchCanCloseUpgradeModal",
      "failed, default allow close",
      { cause: String(error) },
    );
    return true;
  }
}

export type AppVersionUpdateModalDecision = {
  shouldShow: boolean;
  canCloseUpgradeModal: boolean;
};

export async function resolveAppVersionUpdateModal(
  now: number = Date.now(),
): Promise<AppVersionUpdateModalDecision> {
  const currentBuild = getCurrentAppBuildNumber();
  const [remoteMinBuild, canCloseUpgradeModal] = await Promise.all([
    fetchRemoteMinBuildForPlatform(),
    fetchCanCloseUpgradeModal(),
  ]);

  if (currentBuild == null || remoteMinBuild == null) {
    return { shouldShow: false, canCloseUpgradeModal };
  }
  if (!needsAppVersionUpdate(currentBuild, remoteMinBuild)) {
    return { shouldShow: false, canCloseUpgradeModal };
  }

  if (!canCloseUpgradeModal) {
    return { shouldShow: true, canCloseUpgradeModal: false };
  }

  const lastShown = getItem(
    LOCAL_STORAGE_KEYS.APP_VERSION_UPDATE_MODAL_LAST_SHOWN,
  ) as string | number | null | undefined;

  return {
    shouldShow: canShowAppVersionUpdateByInterval(lastShown, now),
    canCloseUpgradeModal: true,
  };
}

export async function shouldShowAppVersionUpdateModal(
  now: number = Date.now(),
): Promise<boolean> {
  const decision = await resolveAppVersionUpdateModal(now);
  return decision.shouldShow;
}
